import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Filter, Check } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import usePageTitle from '../hooks/usePageTitle';
import { LAB_PANELS, BRAND_INFO } from '../data/mock';
import { useCart } from '../context/CartContext';

const tabs = ['All', 'Men', 'Women', 'On-cycle', 'Recovery'];

const PanelsPage = () => {
  usePageTitle(
    `Lab Panels | Order Bloodwork Online | ${BRAND_INFO.name}`,
    'Order professionally curated bloodwork panels for athletes — baseline, cycle safety, complete men\u2019s, women\u2019s, PCOS, thyroid, and recovery panels.'
  );

  const [tab, setTab] = useState('All');
  const [sort, setSort] = useState('featured');
  const { addPanel, items } = useCart();
  const inCart = (id) => items.some((it) => it.kind === 'panel' && it.id === id);

  const filtered = useMemo(() => {
    let items = [...LAB_PANELS];
    if (tab === 'Men') items = items.filter((p) => /men|cycle|complete-men|thyroid|recovery|baseline$/i.test(p.id));
    if (tab === 'Women') items = items.filter((p) => /women|pcos/i.test(p.id));
    if (tab === 'On-cycle') items = items.filter((p) => /cycle/i.test(p.id));
    if (tab === 'Recovery') items = items.filter((p) => /recovery|thyroid/i.test(p.id));
    if (sort === 'low') items.sort((a, b) => a.price - b.price);
    if (sort === 'high') items.sort((a, b) => b.price - a.price);
    return items;
  }, [tab, sort]);

  return (
    <>
      <Header />

      {/* Banner */}
      <section className="pt-28 lg:pt-36 pb-12 bg-cream-100">
        <div className="container-wide">
          <div className="max-w-3xl">
            <p className="eyebrow">Order Labs</p>
            <h1 className="mt-5 font-display text-display-lg text-ink-900">
              Pick a panel.{' '}
              <em className="italic text-brick-500">Skip the guesswork.</em>
            </h1>
            <p className="mt-5 text-lg text-ink-600">
              Curated panels for athletes, lifters, and performance-focused humans. Every
              requisition is physician-signed and drawn at a participating Quest, LabCorp,
              or BioReference location near you.
            </p>
          </div>
        </div>
      </section>

      {/* Sticky toolbar */}
      <div className="sticky top-16 lg:top-20 z-30 bg-cream-100/92 backdrop-blur-md border-y border-cream-200">
        <div className="container-wide py-4 flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
          <div className="flex items-center gap-2 overflow-x-auto -mx-1 px-1">
            {tabs.map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`whitespace-nowrap px-4 py-2 rounded-full border text-[14px] transition-colors ${
                  tab === t
                    ? 'bg-ink-900 text-cream-50 border-ink-900'
                    : 'bg-cream-50 border-cream-300 text-ink-700 hover:border-ink-900'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-1.5 text-[12px] text-ink-500">
              <Filter className="w-3.5 h-3.5" /> Sort
            </span>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-4 py-2 text-[14px] rounded-full bg-cream-50 border border-cream-300 text-ink-800"
            >
              <option value="featured">Featured</option>
              <option value="low">Price: low to high</option>
              <option value="high">Price: high to low</option>
            </select>
          </div>
        </div>
      </div>

      {/* Panels grid */}
      <section className="py-14 bg-cream-100">
        <div className="container-wide">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((p) => (
              <article
                key={p.id}
                className="group relative bg-cream-50 border border-cream-200 rounded-3xl p-7 hover:border-brick-300 hover:shadow-card transition-all flex flex-col"
              >
                <div className="flex items-start justify-between">
                  <h3 className="font-display text-2xl text-ink-900 max-w-[68%] leading-tight">{p.name}</h3>
                  <div className="text-right">
                    <div className="text-[10px] tracking-[0.18em] uppercase text-ink-400">From</div>
                    <div className="font-display text-2xl text-ink-900">${p.price}</div>
                  </div>
                </div>
                <div className="mt-3 text-[10px] tracking-[0.18em] uppercase text-ink-400">{p.badge}</div>
                <p className="mt-5 text-ink-700 text-[15px] leading-relaxed">{p.description}</p>

                <ul className="mt-5 space-y-2 text-[14px] text-ink-700">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5">
                      <span className="mt-1 inline-flex w-3 h-3 rounded bg-sage-300"></span>
                      {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-7 pt-5 border-t border-cream-200 flex items-center justify-between">
                  <div className="text-[12px] text-ink-500">SKU: {p.sku}</div>
                  <button
                    onClick={() => addPanel(p)}
                    className={`inline-flex items-center gap-2 hover:gap-3 transition-all rounded-full px-4 py-2 text-[14px] ${
                      inCart(p.id)
                        ? 'bg-sage-500 text-white border border-sage-500'
                        : 'text-brick-500 border border-brick-200 hover:bg-brick-50'
                    }`}
                  >
                    {inCart(p.id) ? (
                      <>Added <Check className="w-3.5 h-3.5" /></>
                    ) : (
                      <>Add to order <ArrowRight className="w-3.5 h-3.5" /></>
                    )}
                  </button>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-14 p-8 lg:p-10 rounded-3xl bg-charcoal-900 text-cream-50 grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-8">
              <h3 className="font-display text-display-sm">
                Don&apos;t see the panel you want?{' '}
                <em className="italic text-brick-300">Build it yourself.</em>
              </h3>
              <p className="mt-3 text-cream-200/80">
                Browse 140+ markers with live, lab-verified pricing and pay only for what you need.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/biomarkers"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brick-500 hover:bg-brick-600 text-white"
              >
                Build à la carte <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default PanelsPage;
